import { useState, useCallback } from 'react';
import { PListDetailsDto, EmptyPallet } from '../../../../../types/picking';
import { pickingService } from '../../../../../services/pickingService';
import { plistOrderService, PListOrderDto } from '../../../../../services/plistOrderService';
import { shipmentLineService } from '../../../../../services/shipmentLineService';
import { packingService } from '../../../../../services/packingService';
import { ShipmentLineRow } from '../../../../../types/shipmentLine';
import { UserProfileDto } from '../../../../../types/userProfile';
import { ExtendedPickingLine } from './usePickingData';

export interface UsePickingCompletionReturn {
  // Modal states
  showDeliveryInfoModal: boolean;
  setShowDeliveryInfoModal: React.Dispatch<React.SetStateAction<boolean>>;
  showPackZoneSelection: boolean;
  setShowPackZoneSelection: React.Dispatch<React.SetStateAction<boolean>>;
  showQuantityDeviation: boolean;
  quantityDeviations: Array<{
    pListLineNo: number;
    itemNo: string;
    itemDescription: string;
    qtyOrdered: number;
    qtyPicked: number;
    deviation: number;
  }>;
  showSignatureModal: boolean;
  setShowSignatureModal: React.Dispatch<React.SetStateAction<boolean>>;

  // Handlers
  handleCompletePicking: () => Promise<void>;
  handleSendToPacking: () => Promise<void>;
  handlePackZoneConfirm: (packZoneID: string) => Promise<void>;
  handleDeliveryInfoConfirm: (
    pListData: DeliveryInfoPListData,
    pListOrdersData: Map<number, DeliveryInfoPListData>,
    shipmentLinesData?: Map<number, ShipmentLineRow[]>,
    consolidate?: boolean
  ) => Promise<void>;
  handleQuantityDeviationConfirm: () => void;
  handleQuantityDeviationCancel: () => void;
  handleSignatureConfirm: (signatureDataUrl: string) => Promise<void>;
}

export interface DeliveryInfoPListData {
  totalVolume: string;
  totalWeight: string;
  numOfPackages: string;
  numOfPallets: string;
  carrierName: string;
  deliveryMethodNo: number | null;
  packZoneID: string;
  packageRemark: string;
}

interface UsePickingCompletionDeps {
  pListId: number;
  pickingDetails: PListDetailsDto | null;
  pickingLines: ExtendedPickingLine[];
  pListOrders: PListOrderDto[];
  setPListOrders: React.Dispatch<React.SetStateAction<PListOrderDto[]>>;
  emptyPallets: EmptyPallet[];
  loadPickingDetails: (profile?: any) => Promise<void>;
  onBack: () => void;
}

export function usePickingCompletion(deps: UsePickingCompletionDeps): UsePickingCompletionReturn {
  const {
    pListId,
    pickingDetails,
    pickingLines,
    pListOrders,
    setPListOrders,
    emptyPallets,
    loadPickingDetails,
    onBack,
  } = deps;

  // Modal states
  const [showDeliveryInfoModal, setShowDeliveryInfoModal] = useState(false);
  const [showPackZoneSelection, setShowPackZoneSelection] = useState(false);
  const [showQuantityDeviation, setShowQuantityDeviation] = useState(false);
  const [quantityDeviations, setQuantityDeviations] = useState<Array<{
    pListLineNo: number;
    itemNo: string;
    itemDescription: string;
    qtyOrdered: number;
    qtyPicked: number;
    deviation: number;
  }>>([]);
  const [showSignatureModal, setShowSignatureModal] = useState(false);

  // ─────────────────────────────────────────────────
  // Internal helpers
  // ─────────────────────────────────────────────────

  const checkEmptyPallets = useCallback((): boolean => {
    if (emptyPallets.length > 0) {
      const confirmDelete = window.confirm(
        `Du har ${emptyPallets.length} tomme paller som ikke er brukt.\n\n` +
        `Tomme paller lagres ikke i systemet og vil forsvinne når du fullfører plukking.\n\n` +
        `Vil du slette disse og fortsette?\n\n` +
        `Velg "OK" for å slette tomme paller og fullføre.\n` +
        `Velg "Avbryt" for å gå tilbake.`
      );

      if (!confirmDelete) {
        return false;
      }
      console.log(`Brukeren bekreftet sletting av ${emptyPallets.length} tomme paller`);
    }
    return true;
  }, [emptyPallets]);

  const validateSSCC = useCallback(async (action: string): Promise<boolean> => {
    try {
      const ssccValidation = await pickingService.validateSSCCForCompletion(pListId);
      if (!ssccValidation.isValid) {
        alert(
          `Kan ikke ${action}.\n\n` +
          `${ssccValidation.message}\n\n` +
          `Skriv ut SSCC-etiketter for disse ordrene før du ${action === 'fullføre plukklisten' ? 'fullfører' : 'sender til pakking'}.`
        );
        return false;
      }
    } catch (err) {
      // Fail-open: if validation endpoint fails, allow completion to proceed
      console.error('Error validating SSCC (continuing):', err);
    }
    return true;
  }, [pListId]);

  const getDeviationLines = useCallback(() => {
    return pickingLines
      .filter(line => line.qtyPicked !== line.qty)
      .map(line => ({
        pListLineNo: line.pListLineNo,
        itemNo: line.itemNo || '',
        itemDescription: line.itemDescription || 'Ukjent vare',
        qtyOrdered: line.qty,
        qtyPicked: line.qtyPicked,
        deviation: line.qtyPicked - line.qty
      }));
  }, [pickingLines]);

  const proceedToDeliveryInfo = useCallback(async () => {
    // Reload PListOrders to ensure we have the latest data before opening modal
    try {
      const companyNo = pickingDetails!.header.companyNo;
      console.log('Reloading PListOrders before opening modal...');
      const orders = await plistOrderService.getByPListId(companyNo, pListId);
      console.log('Reloaded PListOrders:', orders);
      setPListOrders(orders);
    } catch (error) {
      console.error('Error reloading PListOrders:', error);
    }

    setShowDeliveryInfoModal(true);
  }, [pListId, pickingDetails, setPListOrders]);

  // ─────────────────────────────────────────────────
  // Public handlers
  // ─────────────────────────────────────────────────

  const handleCompletePicking = useCallback(async () => {
    if (!checkEmptyPallets()) return;
    if (!(await validateSSCC('fullføre plukklisten'))) return;

    const deviationLines = getDeviationLines();

    if (deviationLines.length > 0) {
      setQuantityDeviations(deviationLines);
      setShowQuantityDeviation(true);
      return;
    }

    await proceedToDeliveryInfo();
  }, [checkEmptyPallets, validateSSCC, getDeviationLines, proceedToDeliveryInfo]);

  const handleSendToPacking = useCallback(async () => {
    // Check for unused empty pallets
    if (emptyPallets.length > 0) {
      const confirmDelete = window.confirm(
        `Du har ${emptyPallets.length} tomme paller som ikke er brukt.\n\n` +
        `Tomme paller lagres ikke i systemet og vil forsvinne når du sender til pakking.\n\n` +
        `Vil du slette disse og fortsette?\n\n` +
        `Velg "OK" for å slette tomme paller og fortsette.\n` +
        `Velg "Avbryt" for å gå tilbake.`
      );

      if (!confirmDelete) {
        return;
      }
      console.log(`Brukeren bekreftet sletting av ${emptyPallets.length} tomme paller`);
    }

    if (!(await validateSSCC('sende til pakking'))) return;

    // Check for quantity deviations (but allow them for "Til Pakking")
    const deviationLines = getDeviationLines();

    if (deviationLines.length > 0) {
      const deviationSummary = deviationLines.map(line =>
        `${line.itemDescription}: ${line.qtyPicked} av ${line.qtyOrdered}`
      ).join('\n');

      const confirmed = window.confirm(
        `Det er avvik på ${deviationLines.length} linje(r):\n\n${deviationSummary}\n\n` +
        `Vil du sende til pakking med disse avvikene?\n\n` +
        `Velg "OK" for å fortsette.\n` +
        `Velg "Avbryt" for å gå tilbake.`
      );

      if (!confirmed) {
        return;
      }
    }

    // Reload PListOrders and open pack zone selection modal
    try {
      const companyNo = pickingDetails!.header.companyNo;
      const orders = await plistOrderService.getByPListId(companyNo, pListId);
      setPListOrders(orders);
    } catch (err) {
      console.error('Error reloading PListOrders:', err);
    }
    setShowPackZoneSelection(true);
  }, [emptyPallets, validateSSCC, getDeviationLines, pListId, pickingDetails, setPListOrders]);

  const handlePackZoneConfirm = useCallback(async (packZoneID: string) => {
    try {
      const companyNo = pickingDetails!.header.companyNo;

      // Update PackZoneID on all PListOrders
      for (const order of pListOrders) {
        await plistOrderService.update(companyNo, pListId, order.orderNo, {
          packZoneID: packZoneID
        });
      }

      // Send to packing
      await pickingService.sendToPacking(pListId);

      setShowPackZoneSelection(false);
      alert('Plukklisten er sendt til pakking!');
      onBack();
    } catch (error) {
      console.error('Error sending to packing:', error);
      throw error;
    }
  }, [pListId, pickingDetails, pListOrders, onBack]);

  const handleDeliveryInfoConfirm = useCallback(async (
    pListData: DeliveryInfoPListData,
    pListOrdersData: Map<number, DeliveryInfoPListData>,
    shipmentLinesData?: Map<number, ShipmentLineRow[]>,
    consolidate?: boolean
  ) => {
    try {
      // Update PList with delivery info
      if (pickingDetails) {
        const updatedPList = {
          ID: pickingDetails.header.id,
          CompanyNo: pickingDetails.header.companyNo,
          PListID: pickingDetails.header.pListID,
          Description1: pickingDetails.header.description1,
          Description2: pickingDetails.header.description2,
          PListDate: pickingDetails.header.pListDate,
          NumOfOrderLines: pickingDetails.header.numOfOrderLines,
          Weight: pickingDetails.header.weight,
          Priority: pickingDetails.header.priority,
          PListStatus: pickingDetails.header.pListStatus,
          OperatorID: pickingDetails.header.operatorID,
          LockedBy: pickingDetails.header.lockedBy,
          StartWorkDateTime: pickingDetails.header.startWorkDateTime,
          StopWorkDateTime: pickingDetails.header.stopWorkDateTime,
          DeliveryDate: pickingDetails.header.deliveryDate,
          TotalVolume: pListData.totalVolume ? parseFloat(pListData.totalVolume) : null,
          TotalWeight: pListData.totalWeight ? parseFloat(pListData.totalWeight) : null,
          NumOfPackages: pListData.numOfPackages ? parseInt(pListData.numOfPackages) : null,
          NumOfPallets: pListData.numOfPallets ? parseFloat(pListData.numOfPallets) : null,
          CarrierName: pListData.carrierName || null,
          DeliveryMethodNo: pListData.deliveryMethodNo,
          PackZoneID: pListData.packZoneID || null,
          PackageRemark: pListData.packageRemark || null
        };

        console.log('Updating PList with data:', updatedPList);
        await pickingService.updatePickingList(pListId, updatedPList);
        console.log('PList updated successfully');
      }

      // Update PListOrders with delivery info
      for (const [orderNo, orderData] of Array.from(pListOrdersData.entries())) {
        const updateRequest = {
          totalVolume: orderData.totalVolume ? parseFloat(orderData.totalVolume) : null,
          totalWeight: orderData.totalWeight ? parseFloat(orderData.totalWeight) : null,
          numOfPackages: orderData.numOfPackages ? parseInt(orderData.numOfPackages) : null,
          numOfPallets: orderData.numOfPallets ? parseFloat(orderData.numOfPallets) : null,
          carrierName: orderData.carrierName || null,
          deliveryMethodNo: orderData.deliveryMethodNo,
          packZoneID: orderData.packZoneID || null,
          packageRemark: orderData.packageRemark || null
        };

        await plistOrderService.update(
          pickingDetails!.header.companyNo,
          pListId,
          orderNo,
          updateRequest
        );
      }

      // Create consolidation Shipment if requested (samlasting)
      if (consolidate && pListOrders.length > 1) {
        console.log('Creating consolidation shipment for samlasting...');
        const firstOrder = pListOrders[0];
        const consolidationResponse = await packingService.createConsolidationShipment({
          pListID: pListId,
          orderNos: pListOrders.map(o => o.orderNo),
          customerNo: firstOrder.customerNo,
          delCustomerName: firstOrder.delName,
          delAddress1: firstOrder.delAddress1,
          delAddress2: firstOrder.delAddress2,
          delZipCode: firstOrder.delPostNo,
          delCity: firstOrder.delCity,
          delCountryCode: firstOrder.delCountry
        });
        console.log('Consolidation shipment created:', consolidationResponse);
      }

      // Save ShipmentLines if provided (kolli-dimensjoner for TA-integrasjon)
      if (shipmentLinesData && shipmentLinesData.size > 0) {
        console.log('Saving shipment lines for orders...');
        for (const [orderNo, lines] of Array.from(shipmentLinesData.entries())) {
          if (lines.length > 0) {
            await shipmentLineService.saveAll({
              pListID: pListId,
              orderNo: orderNo,
              lines: lines.map((line, index) => ({
                lineID: line.lineID || undefined,
                transitPackagingID: line.transitPackagingID,
                description1: line.description1,
                numOfPackages: line.numOfPackages,
                weight: line.weight,
                volume: line.volume,
                height: line.height,
                width: line.width,
                length: line.length,
                sorting: line.sorting ?? index
              }))
            });
            console.log(`Saved ${lines.length} shipment lines for order ${orderNo}`);
          }
        }
      }

      // Complete picking after delivery info is saved
      await pickingService.completePicking(pListId);

      // Close modal and go back
      setShowDeliveryInfoModal(false);
      onBack();
    } catch (error) {
      console.error('Error saving delivery info and completing picking:', error);
      throw error;
    }
  }, [pListId, pickingDetails, pListOrders, onBack]);

  const handleQuantityDeviationConfirm = useCallback(() => {
    setShowQuantityDeviation(false);
    proceedToDeliveryInfo();
  }, [proceedToDeliveryInfo]);

  const handleQuantityDeviationCancel = useCallback(() => {
    setShowQuantityDeviation(false);
  }, []);

  const handleSignatureConfirm = useCallback(async (signatureDataUrl: string) => {
    try {
      if (!pickingDetails) return;

      const updatedPList = {
        ID: pickingDetails.header.id,
        CompanyNo: pickingDetails.header.companyNo,
        PListID: pickingDetails.header.pListID,
        Description1: pickingDetails.header.description1,
        Description2: pickingDetails.header.description2,
        PListDate: pickingDetails.header.pListDate,
        NumOfOrderLines: pickingDetails.header.numOfOrderLines,
        Weight: pickingDetails.header.weight,
        Priority: pickingDetails.header.priority,
        PListStatus: pickingDetails.header.pListStatus,
        OperatorID: pickingDetails.header.operatorID,
        LockedBy: pickingDetails.header.lockedBy,
        StartWorkDateTime: pickingDetails.header.startWorkDateTime,
        StopWorkDateTime: pickingDetails.header.stopWorkDateTime,
        DeliveryDate: pickingDetails.header.deliveryDate,
        Signature1: signatureDataUrl
      };

      console.log('Updating PList with signature');
      await pickingService.updatePickingList(pListId, updatedPList);
      console.log('Signature saved successfully');

      await loadPickingDetails();

      alert('Signatur lagret!');
    } catch (error) {
      console.error('Error saving signature:', error);
      throw error;
    }
  }, [pListId, pickingDetails, loadPickingDetails]);

  return {
    // Modal states
    showDeliveryInfoModal,
    setShowDeliveryInfoModal,
    showPackZoneSelection,
    setShowPackZoneSelection,
    showQuantityDeviation,
    quantityDeviations,
    showSignatureModal,
    setShowSignatureModal,

    // Handlers
    handleCompletePicking,
    handleSendToPacking,
    handlePackZoneConfirm,
    handleDeliveryInfoConfirm,
    handleQuantityDeviationConfirm,
    handleQuantityDeviationCancel,
    handleSignatureConfirm,
  };
}
